<template>
  <Select
    :model-value="modelValue"
    :input-id="id"
    :disabled="disabled"
    :loading="carriers.loading"
    :placeholder="t(labelKey)"
    :options="carriers.data"
    :option-label="carrierLabel"
    :filter-match-mode="SERVER_SEARCH_MATCH_MODE"
    :filter-placeholder="t('common.findParam', { param: t(labelKey) })"
    :empty-message="emptyMessage"
    :empty-filter-message="emptyMessage"
    data-key="id"
    class="carrier-quick-search"
    overlay-class="carrier-quick-search-overlay"
    filter
    reset-filter-on-clear
    reset-filter-on-hide
    show-clear
    :virtual-scroller-options="{
      itemSize: 31,
      lazy: true,
      showLoader: true,
      loading: !carriers.data?.length && carriers.loading,
      onLazyLoad,
    }"
    @before-show="loadNextPage"
    @filter="onSearch($event.value)"
    @change="onChange"
    @hide="onHide"
  >
    <template #value="{ value, placeholder }">
      {{ value ? carrierLabel(value) : placeholder }}
    </template>
    <template v-if="slots.footer" #footer="slotProps">
      <slot name="footer" v-bind="slotProps" />
    </template>
  </Select>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { CapacityProviderType } from '@rygen/rygen-ui/model';

import { SERVER_SEARCH_MATCH_MODE, useCarrierSearch } from '@/composables';
import { Carrier } from '@/model';
import { carrierLabel } from '@/util';

import Select, { SelectChangeEvent } from 'primevue/select';

const props = withDefaults(
  defineProps<{
    id: string;
    modelValue?: Carrier;
    disabled?: boolean;
    searchType?: 'assigned' | 'all';
    type?: CapacityProviderType;
    /** i18n key for what is being picked, e.g. `brokerages.brokers`. */
    labelKey?: string;
  }>(),
  {
    disabled: false,
    searchType: 'assigned',
    modelValue: undefined,
    type: undefined,
    labelKey: 'carriers.carriers',
  },
);
const { modelValue, searchType, type, labelKey } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: Carrier | undefined): void;
  (event: 'change', payload: SelectChangeEvent): void;
  (event: 'clear'): void;
}>();

const slots = defineSlots<{
  footer?: (props: { value: Carrier; options: Carrier[] }) => any;
}>();

const { t } = useI18n();
const { carriers, loadNextPage, onLazyLoad, onSearch, onHide } =
  useCarrierSearch({ searchType, type });

const emptyMessage = computed(() => {
  if (carriers.value.loading) {
    return t('common.loading');
  }

  return carriers.value.error
    ? t('errors.loadingParam', { param: t(labelKey.value, 2) })
    : t('common.noParamMatchingSearch', { param: t(labelKey.value, 2) });
});

const onChange = (event: SelectChangeEvent) => {
  const carrier = event.value ? new Carrier(event.value) : undefined;
  emit('update:model-value', carrier);

  if (carrier) {
    emit('change', { ...event, value: carrier });
  } else {
    emit('clear');
  }
};
</script>

<style>
.carrier-quick-search {
  width: 100%;

  &.p-inputwrapper-filled:not(.p-disabled) .p-select-label.p-placeholder {
    color: var(--p-select-color);
  }
}

.carrier-quick-search-overlay.p-select-overlay {
  .p-select-option {
    display: block;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
}
</style>
